import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FileManager } from '@cubone/react-file-manager';
import { FolderPlus, Grid2X2, List, Loader2, RefreshCw, Upload } from 'lucide-react';
import '@cubone/react-file-manager/dist/style.css';
import '../../styles/cubone-overrides.css';
import {
  createDocumentFolder, deleteDocumentNode, downloadDocumentFile, listDocumentNodes,
  moveDocumentNode, renameDocumentNode, zipDocumentNodes,
} from '../../services/documents';
import type { DocumentCapabilities, DocumentSpace } from '../../types/documents';
import { flattenTree, type CuboneFile } from '../../utils/documentNodes';
import { toDocumentManagerPermissions } from '../../utils/documentCapabilities';
import { parseContentDispositionFilename, saveBlobAs } from '../../utils/downloadHelpers';
import { useToast } from '../ui/use-toast';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { useDocumentUploads } from '../../contexts/DocumentUploadContext';
import { useDocumentIndexing } from '../../contexts/DocumentIndexingContext';
import { BibliotecarioQueue } from './BibliotecarioQueue';
import { BibliotecarioProposalPanel } from './BibliotecarioProposalPanel';
import { BibliotecarioConfigurationDialog } from './BibliotecarioConfigurationDialog';
import { useOptionalResourceSubscription } from '../../contexts/NotificationsContext';
import type { BibliotecarioJob } from '../../types/bibliotecario';

export type DocumentFileManagerMode = 'full' | 'embedded';

export interface DocumentFileManagerProps {
  space: DocumentSpace;
  currentFolder: CuboneFile | null;
  selectedFiles: CuboneFile[];
  capabilities: DocumentCapabilities;
  mode?: DocumentFileManagerMode;
  onFolderChange: (folder: CuboneFile | null) => void;
  onSelectionChange: (files: CuboneFile[]) => void;
  onFileOpen?: (file: CuboneFile) => void;
  externalRevision?: number;
  onLoadStateChange?: (state: { loading: boolean; error: boolean; empty: boolean }) => void;
}

const isDescendantOfAny = (node: CuboneFile, nodes: CuboneFile[]) =>
  nodes.some((candidate) => candidate !== node && node.path.startsWith(`${candidate.path}/`));

/** Adapter Cubone condiviso fra archivio documentale e superfici incorporate. */
export function DocumentFileManager({
  space, currentFolder, selectedFiles, capabilities, mode = 'full',
  onFolderChange, onSelectionChange, onFileOpen, onLoadStateChange, externalRevision = 0,
}: DocumentFileManagerProps) {
  const [files, setFiles] = useState<CuboneFile[]>([]);
  const [pathToId, setPathToId] = useState<Map<string, number>>(new Map());
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [layout, setLayout] = useState<'grid' | 'list'>('grid');
  const [createOpen, setCreateOpen] = useState(false);
  const [folderName, setFolderName] = useState('');
  const [creatingFolder, setCreatingFolder] = useState(false);
  const [bibliotecarioJob, setBibliotecarioJob] = useState<BibliotecarioJob | null>(null);
  const [bibliotecarioRevision, setBibliotecarioRevision] = useState(0);
  const [bibliotecarioConfigOpen, setBibliotecarioConfigOpen] = useState(false);
  const uploadInput = useRef<HTMLInputElement>(null);
  const { toast } = useToast();
  const { enqueue, revisionBySpace } = useDocumentUploads();
  const { states: indexStates, watch } = useDocumentIndexing();

  const processingIds = useMemo(() => files
    .filter((file) => !file.isDirectory && (file.indexStatus === 'pending' || file.indexStatus === 'indexing'))
    .map((file) => file._id), [files]);

  const managerPermissions = useMemo(
    () => ({ ...toDocumentManagerPermissions(capabilities), create: false, upload: false }),
    [capabilities],
  );

  const refresh = useCallback(async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const flat = flattenTree(await listDocumentNodes(space.id));
      setFiles(flat.files);
      setPathToId(flat.pathToId);
    } catch (error) {
      console.error('[DocumentFileManager] load failed', error);
      setLoadError(true);
      toast({ title: 'Caricamento documenti fallito', description: 'Riprova piu tardi.' });
    } finally {
      setLoading(false);
    }
  }, [space.id, toast]);

  useEffect(() => {
    onLoadStateChange?.({ loading, error: loadError, empty: !loading && !loadError && files.length === 0 });
  }, [files.length, loadError, loading, onLoadStateChange]);

  useEffect(() => {
    refresh();
  }, [space.id, refresh, revisionBySpace[space.id], externalRevision]);

  useEffect(() => {
    if (!processingIds.length) return;
    return watch(processingIds);
  }, [processingIds.join(','), watch]);

  useEffect(() => {
    setFiles((current) => current.map((file) => {
      const state = indexStates[file._id];
      return state ? { ...file, indexStatus: state.status, indexReasonCode: state.reasonCode, indexReason: state.reason, indexUpdatedAt: state.updatedAt, indexedAt: state.indexedAt } : file;
    }));
  }, [indexStates]);

  useEffect(() => {
    const changed = selectedFiles.some((file) => {
      const state = indexStates[file._id];
      return state && state.status !== file.indexStatus;
    });
    if (changed) onSelectionChange(selectedFiles.map((file) => {
      const state = indexStates[file._id];
      return state ? { ...file, indexStatus: state.status, indexReasonCode: state.reasonCode, indexReason: state.reason, indexUpdatedAt: state.updatedAt, indexedAt: state.indexedAt } : file;
    }));
  }, [indexStates, onSelectionChange, selectedFiles]);

  const folderId = (folder?: CuboneFile): number | null =>
    folder?.path ? pathToId.get(folder.path) ?? null : null;
  const documentFolders = useMemo(() => files.filter((file) => file.isDirectory), [files]);
  const refreshBibliotecario = useCallback(() => {
    setBibliotecarioRevision((value) => value + 1);
    void refresh();
  }, [refresh]);
  useOptionalResourceSubscription('bibliotecario_job', refreshBibliotecario, [refreshBibliotecario]);

  const handleCreateFolder = async (name: string, parent: CuboneFile) => {
    await createDocumentFolder(space.id, name, folderId(parent));
    await refresh();
  };

  const createFolderFromToolbar = async () => {
    const name = folderName.trim();
    if (!name) return;
    setCreatingFolder(true);
    try {
      await createDocumentFolder(space.id, name, currentFolder?._id ?? null);
      await refresh();
      setFolderName('');
      setCreateOpen(false);
    } catch (error) {
      console.error('[DocumentFileManager] create folder failed', error);
      toast({ title: 'Creazione cartella fallita', description: 'Controlla il nome e riprova.' });
    } finally {
      setCreatingFolder(false);
    }
  };

  const handleRename = async (file: CuboneFile, name: string) => {
    await renameDocumentNode(file._id, name);
    await refresh();
  };

  const handlePaste = async (sources: CuboneFile[], destination: CuboneFile, operation: 'copy' | 'move') => {
    if (operation !== 'move') return;
    const targets = sources.filter((node) => !isDescendantOfAny(node, sources));
    await Promise.allSettled(targets.map((node) => moveDocumentNode(node._id, folderId(destination))));
    await refresh();
  };

  const handleDelete = async (nodes: CuboneFile[]) => {
    const targets = nodes.filter((node) => !isDescendantOfAny(node, nodes));
    await Promise.allSettled(targets.map((node) => deleteDocumentNode(node._id)));
    onSelectionChange(selectedFiles.filter((selected) => !targets.some((node) => node._id === selected._id)));
    await refresh();
  };

  const handleDownload = async (nodes: CuboneFile[]) => {
    if (nodes.length === 0) return;
    const targets = nodes.filter((node) => !isDescendantOfAny(node, nodes));
    const folders = targets.filter((node) => node.isDirectory);
    const regularFiles = targets.filter((node) => !node.isDirectory);
    if (folders.length === 0 && regularFiles.length === 1) {
      try {
        const { data } = await downloadDocumentFile(regularFiles[0]._id);
        saveBlobAs(data, regularFiles[0].name);
      } catch (error) {
        console.error('[DocumentFileManager] download failed', error);
        toast({ title: 'Download fallito', description: 'Riprova piu tardi.' });
      }
      return;
    }
    toast({ title: 'Preparazione ZIP...' });
    try {
      const response = await zipDocumentNodes(targets.map((node) => node._id));
      const filename = parseContentDispositionFilename(response.headers['content-disposition']) || 'download.zip';
      saveBlobAs(response.data, filename);
    } catch (error) {
      let errorCode: string | null = null;
      const responseData = (error as { response?: { data: Blob } }).response?.data;
      if (responseData instanceof Blob) {
        try { errorCode = JSON.parse(await responseData.text())?.error ?? null; } catch { /* non JSON */ }
      }
      const messages: Record<string, [string, string]> = {
        selection_too_large: ['Selezione troppo grande', 'Riduci il numero di file o la dimensione totale e riprova.'],
        empty_selection: ['Nessun file da scaricare', 'La selezione non contiene file.'],
        all_orphans_or_empty: ['File non piu disponibili', 'I file selezionati non sono piu presenti su storage.'],
      };
      const [title, description] = messages[errorCode ?? ''] ?? ['Download fallito', 'Riprova piu tardi.'];
      toast({ title, description });
    }
  };

  const handleFolderChange = (path: string) => {
    onFolderChange(path ? files.find((file) => file.isDirectory && file.path === path) ?? null : null);
  };

  return (
    <div tabIndex={-1} data-cubone-root data-document-file-manager-mode={mode} data-space-id={space.id} className="file-manager-wrapper">
      <div className="hexa-file-toolbar" role="toolbar" aria-label="Azioni documenti">
        <input ref={uploadInput} className="sr-only" aria-label="Seleziona documenti da caricare" type="file" multiple onChange={(event) => {
          const selected = Array.from(event.target.files ?? []);
          if (selected.length) enqueue(selected, space.id, currentFolder?._id ?? null);
          event.target.value = '';
        }} />
        {capabilities.create && <Button type="button" size="sm" variant="outline" onClick={() => setCreateOpen(true)}><FolderPlus className="mr-1 h-4 w-4" />Nuova cartella</Button>}
        {capabilities.upload && <Button type="button" size="sm" onClick={() => uploadInput.current?.click()}><Upload className="mr-1 h-4 w-4" />Carica</Button>}
        {mode === 'full' && currentFolder && capabilities.create && <Button type="button" size="sm" variant="outline" onClick={() => setBibliotecarioConfigOpen(true)}>Usa come Sala Posta</Button>}
        <span className="hexa-file-toolbar-spacer" />
        {selectedFiles.length === 1 && selectedFiles[0].indexStatus && !selectedFiles[0].isDirectory && <span className={`hexa-index-status hexa-index-status--${selectedFiles[0].indexStatus}`} title={selectedFiles[0].indexReason || selectedFiles[0].indexStatus}>{selectedFiles[0].indexStatus === 'indexed' ? 'Indicizzato' : selectedFiles[0].indexStatus === 'indexing' ? 'Indicizzazione in corso' : selectedFiles[0].indexStatus === 'pending' ? 'In attesa' : 'Problema indicizzazione'}</span>}
        <Button type="button" size="icon" variant="ghost" onClick={refresh} disabled={loading} aria-label="Aggiorna documenti" title="Aggiorna documenti"><RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} /></Button>
        <Button type="button" size="icon" variant="ghost" onClick={() => setLayout((value) => value === 'grid' ? 'list' : 'grid')} aria-label={layout === 'grid' ? 'Passa alla vista elenco' : 'Passa alla vista griglia'} title={layout === 'grid' ? 'Vista elenco' : 'Vista griglia'}>{layout === 'grid' ? <List className="h-4 w-4" /> : <Grid2X2 className="h-4 w-4" />}</Button>
      </div>
      {mode === 'full' && <div className="px-3 py-2 border-b"><BibliotecarioQueue spaceId={space.id} refreshToken={bibliotecarioRevision} onSelect={setBibliotecarioJob} />{bibliotecarioJob && <BibliotecarioProposalPanel job={bibliotecarioJob} folders={documentFolders} canReview={capabilities.move && capabilities.rename && capabilities.create} onChanged={refreshBibliotecario} onClose={() => setBibliotecarioJob(null)} />}</div>}
      {mode === 'full' && space.space_type === 'ENTITY' && !currentFolder && !loading && !loadError && files.length === 0 && <div role="status" className="space-y-1 px-3 py-5 text-center">
        <p className="font-medium">Nessun documento per questo cliente.</p>
        <p className="text-sm text-[var(--hexa-text-muted)]">{capabilities.upload && capabilities.create ? 'Carica il primo documento oppure crea una cartella usando i pulsanti qui sopra.' : capabilities.upload ? 'Carica il primo documento usando il pulsante Carica.' : capabilities.create ? 'Puoi iniziare creando una cartella.' : 'I documenti condivisi con te compariranno qui.'}</p>
      </div>}
      <FileManager
        key={`${space.id}:${currentFolder?.path ?? ''}:${layout}`}
        files={files}
        initialPath={currentFolder?.path ?? ''}
        isLoading={loading}
        onCreateFolder={handleCreateFolder}
        onRename={handleRename}
        onPaste={handlePaste}
        onDelete={handleDelete}
        onDownload={handleDownload}
        onRefresh={refresh}
        onFolderChange={handleFolderChange}
        onSelectionChange={onSelectionChange}
        onFileOpen={onFileOpen}
        onError={(error: unknown, file?: File) => console.error('[DocumentFileManager]', error, file?.name)}
        permissions={managerPermissions}
        layout={layout}
        onLayoutChange={setLayout}
        collapsibleNav={false}
        primaryColor="var(--hexa-purple)"
        height={mode === 'embedded' ? '500px' : '100%'}
        width="100%"
        language="it-IT"
      />
      <Dialog open={createOpen} onOpenChange={(open) => { if (!creatingFolder) setCreateOpen(open); }}>
        <DialogContent className="document-dialog bg-[var(--hexa-modal-bg)] text-[var(--hexa-text-primary)]">
          <DialogHeader><DialogTitle>Nuova cartella</DialogTitle><DialogDescription>Crea una cartella in {currentFolder?.name || space.name}.</DialogDescription></DialogHeader>
          <form onSubmit={(event) => { event.preventDefault(); void createFolderFromToolbar(); }}>
            <Input autoFocus value={folderName} onChange={(event) => setFolderName(event.target.value)} aria-label="Nome nuova cartella" placeholder="Nome cartella" disabled={creatingFolder} />
            <DialogFooter className="mt-4"><Button type="button" variant="outline" onClick={() => setCreateOpen(false)} disabled={creatingFolder}>Annulla</Button><Button type="submit" disabled={!folderName.trim() || creatingFolder}>{creatingFolder && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}Crea</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
      <BibliotecarioConfigurationDialog open={bibliotecarioConfigOpen} onOpenChange={setBibliotecarioConfigOpen} spaceId={space.id} inboxFolder={currentFolder} folders={documentFolders} canConfigure={capabilities.create} onSaved={refreshBibliotecario} />
    </div>
  );
}

export type { CuboneFile };
